<template>
  <div class="order-detail-tab order-detail-tab--insurance">
    <template v-if="!orderId">
      <a-empty description="暂无订单数据" />
    </template>
    <template v-else-if="!hasInsurance">
      <a-empty description="暂无投保信息" />
    </template>
    <div v-else class="tab-content">
      <a-row :gutter="16">
        <a-col :span="24">
          <div class="insurance-block-title">
            保险概览
            <a-button type="link" class="detail-link" @click="openDetail">查看投保详情</a-button>
          </div>
          <a-descriptions bordered size="small" :column="2">
            <a-descriptions-item label="保险订单ID">{{ insuranceInfo.id || '-' }}</a-descriptions-item>
            <a-descriptions-item label="保险名称">{{ insuranceInfo.name || '-' }}</a-descriptions-item>
            <a-descriptions-item label="支付状态">
              <a-tag :color="bondPayStatusColorMap[insuranceInfo.bondPayStatus] || 'default'">
                {{ bondPayStatusMap[insuranceInfo.bondPayStatus] || '-' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="投保状态">
              <a-tag :color="underwritingStatusColorMap[insuranceInfo.underwritingStatus] || 'default'">
                {{ underwritingStatusMap[insuranceInfo.underwritingStatus] || '-' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="被保人数">{{ insuranceInfo.insuredCount != null ? insuranceInfo.insuredCount : '-' }}</a-descriptions-item>
            <a-descriptions-item label="未投保人数">
              <a-tag v-if="pendingInsuredCount > 0" :color="isWorkStartNearTimeout ? 'red' : 'green'">
                {{ pendingInsuredCount }} 人{{ isWorkStartNearTimeout ? '未投保-超时风险' : '未投保-等待接单中' }}
              </a-tag>
              <span v-else>-</span>
              <div v-if="pendingInsuredCount > 0" class="pending-hint">
                这些师傅接单后未投保（含中途取消），保费份额将在订单完工/取消时退还雇主
              </div>
              <div v-if="insuranceInfo.pendingPremiumAmount" class="pending-hint">
                待结算保费份额：¥{{ insuranceInfo.pendingPremiumAmount }}
              </div>
            </a-descriptions-item>
            <a-descriptions-item label="第三方单号">{{ insuranceInfo.thirdOrderNo || '-' }}</a-descriptions-item>
            <a-descriptions-item label="支付时间">{{ formatDateTime(insuranceInfo.bondPayDate) }}</a-descriptions-item>
            <a-descriptions-item label="最近提交时间">{{ formatDateTime(insuranceInfo.lastSubmitTime) }}</a-descriptions-item>
            <a-descriptions-item label="最近成功时间">{{ formatDateTime(insuranceInfo.lastSuccessTime) }}</a-descriptions-item>
            <a-descriptions-item label="保单" :span="hasPolicyList(insuranceInfo) ? 2 : 1">
              <template v-if="hasPolicyList(insuranceInfo)">
                <div class="policy-summary">{{ policyProgressText(insuranceInfo) }}</div>
                <a-table
                  class="policy-table"
                  :columns="policyColumns"
                  :data-source="policyListRows(insuranceInfo)"
                  :pagination="false"
                  size="small"
                  row-key="id"
                >
                  <template slot="policyInsured" slot-scope="text, record">
                    <div>{{ record.insuredName || '-' }}</div>
                    <div class="policy-card-no">{{ record.insuredIdCard || '-' }}</div>
                  </template>
                  <template slot="policyUnderwritingStatus" slot-scope="text, record">
                    <a-tag :color="underwritingStatusColorMap[record.underwritingStatus] || 'default'">
                      {{ underwritingStatusMap[record.underwritingStatus] || '-' }}
                    </a-tag>
                  </template>
                  <template slot="policyCancelledAfterInsured" slot-scope="text, record">
                    <a-tag :color="cancelledAfterInsuredColorMap[record.cancelledAfterInsured] || 'default'">
                      {{ cancelledAfterInsuredMap[record.cancelledAfterInsured] || '-' }}
                    </a-tag>
                  </template>
                  <template slot="policyInsuranceDepositForfeited" slot-scope="text, record">
                    <a-tag :color="insuranceDepositForfeitedColorMap[record.insuranceDepositForfeited] || 'default'">
                      {{ insuranceDepositForfeitedMap[record.insuranceDepositForfeited] || '-' }}
                    </a-tag>
                  </template>
                  <template slot="policyStatus" slot-scope="text, record">
                    <a-tag :color="record.policyFileUrl ? 'green' : 'orange'">
                      {{ record.policyFileUrl ? '已上传' : '未上传' }}
                    </a-tag>
                    <div v-if="record.policyUploadTime" class="policy-upload-time">
                      {{ formatDateTime(record.policyUploadTime) }}
                    </div>
                  </template>
                  <template slot="policyAction" slot-scope="text, record">
                    <a v-if="record.policyFileUrl" @click="viewPolicy(record)">查看</a>
                    <span v-else>-</span>
                  </template>
                </a-table>
              </template>
              <template v-else>
                <a v-if="insuranceInfo.policyFileUrl" @click="viewPolicy(insuranceInfo)">
                  {{ insuranceInfo.policyFileName || '查看保单' }}
                </a>
                <span v-else>-</span>
              </template>
            </a-descriptions-item>
            <a-descriptions-item v-if="!hasPolicyList(insuranceInfo)" label="保单上传时间">{{ formatDateTime(insuranceInfo.policyUploadTime) }}</a-descriptions-item>
            <a-descriptions-item label="最近失败原因">{{ insuranceInfo.lastFailReason || '-' }}</a-descriptions-item>
          </a-descriptions>
        </a-col>
      </a-row>

      <a-row :gutter="16" class="insurance-section">
        <a-col :span="8">
          <div class="insurance-block-title">投保人信息</div>
          <a-descriptions v-if="applicantInfo" bordered size="small" :column="1">
            <a-descriptions-item label="姓名">{{ applicantInfo.name || '-' }}</a-descriptions-item>
            <a-descriptions-item label="手机号">{{ applicantInfo.mobile || '-' }}</a-descriptions-item>
            <a-descriptions-item label="身份证号">{{ applicantInfo.cardNumber || '-' }}</a-descriptions-item>
            <a-descriptions-item label="性别">{{ applicantInfo.sex || '-' }}</a-descriptions-item>
            <a-descriptions-item label="出生日期">{{ applicantInfo.birthdate || '-' }}</a-descriptions-item>
            <a-descriptions-item label="邮箱">{{ applicantInfo.email || '-' }}</a-descriptions-item>
          </a-descriptions>
          <a-empty v-else description="暂无投保人信息" />
        </a-col>
        <a-col :span="16">
          <div class="insurance-block-title">被保人信息</div>
          <a-table
            v-if="insuredList.length > 0"
            :columns="insuredColumns"
            :data-source="insuredList"
            :pagination="false"
            row-key="tableKey"
          >
            <template slot="sex" slot-scope="sex">
              {{ sexText(sex) }}
            </template>
            <template slot="birthday" slot-scope="birthday, record">
              {{ birthday || parseBirthday(record.idCard) || '-' }}
            </template>
          </a-table>
          <a-empty v-else description="暂无被保人信息" />
        </a-col>
      </a-row>

      <a-modal
        title="投保详情"
        :visible="detailVisible"
        width="900px"
        :footer="null"
        @cancel="closeDetail"
      >
        <a-spin :spinning="detailLoading">
          <a-descriptions bordered size="small" :column="2">
            <a-descriptions-item label="保险订单ID">{{ detailForm.id || '-' }}</a-descriptions-item>
            <a-descriptions-item label="投保状态">
              <a-tag :color="underwritingStatusColorMap[detailForm.underwritingStatus] || 'default'">
                {{ underwritingStatusMap[detailForm.underwritingStatus] || '-' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="第三方单号">{{ detailForm.thirdOrderNo || '-' }}</a-descriptions-item>
            <a-descriptions-item label="已重试次数">{{ detailForm.retryCount != null ? detailForm.retryCount : '-' }}</a-descriptions-item>
            <a-descriptions-item label="最大重试次数">{{ detailForm.maxRetry != null ? detailForm.maxRetry : '-' }}</a-descriptions-item>
            <a-descriptions-item label="最近一次提交时间">{{ formatDateTime(detailForm.lastSubmitTime) }}</a-descriptions-item>
            <a-descriptions-item label="最近一次成功时间">{{ formatDateTime(detailForm.lastSuccessTime) }}</a-descriptions-item>
            <a-descriptions-item label="保单" :span="hasPolicyList(detailForm) ? 2 : 1">
              <template v-if="hasPolicyList(detailForm)">
                <div class="policy-summary">{{ policyProgressText(detailForm) }}</div>
                <a-table
                  class="policy-table"
                  :columns="policyColumns"
                  :data-source="policyListRows(detailForm)"
                  :pagination="false"
                  size="small"
                  row-key="id"
                >
                  <template slot="policyInsured" slot-scope="text, record">
                    <div>{{ record.insuredName || '-' }}</div>
                    <div class="policy-card-no">{{ record.insuredIdCard || '-' }}</div>
                  </template>
                  <template slot="policyUnderwritingStatus" slot-scope="text, record">
                    <a-tag :color="underwritingStatusColorMap[record.underwritingStatus] || 'default'">
                      {{ underwritingStatusMap[record.underwritingStatus] || '-' }}
                    </a-tag>
                  </template>
                  <template slot="policyCancelledAfterInsured" slot-scope="text, record">
                    <a-tag :color="cancelledAfterInsuredColorMap[record.cancelledAfterInsured] || 'default'">
                      {{ cancelledAfterInsuredMap[record.cancelledAfterInsured] || '-' }}
                    </a-tag>
                  </template>
                  <template slot="policyInsuranceDepositForfeited" slot-scope="text, record">
                    <a-tag :color="insuranceDepositForfeitedColorMap[record.insuranceDepositForfeited] || 'default'">
                      {{ insuranceDepositForfeitedMap[record.insuranceDepositForfeited] || '-' }}
                    </a-tag>
                  </template>
                  <template slot="policyStatus" slot-scope="text, record">
                    <a-tag :color="record.policyFileUrl ? 'green' : 'orange'">
                      {{ record.policyFileUrl ? '已上传' : '未上传' }}
                    </a-tag>
                    <div v-if="record.policyUploadTime" class="policy-upload-time">
                      {{ formatDateTime(record.policyUploadTime) }}
                    </div>
                  </template>
                  <template slot="policyAction" slot-scope="text, record">
                    <a v-if="record.policyFileUrl" @click="viewPolicy(record)">查看</a>
                    <span v-else>-</span>
                  </template>
                </a-table>
              </template>
              <template v-else>
                <a v-if="detailForm.policyFileUrl" @click="viewPolicy(detailForm)">
                  {{ detailForm.policyFileName || '查看保单' }}
                </a>
                <span v-else>-</span>
              </template>
            </a-descriptions-item>
            <a-descriptions-item v-if="!hasPolicyList(detailForm)" label="保单上传时间">{{ formatDateTime(detailForm.policyUploadTime) }}</a-descriptions-item>
            <a-descriptions-item label="最近一次失败原因" :span="2">
              <div class="payload-block">{{ detailForm.lastFailReason || '-' }}</div>
            </a-descriptions-item>
            <a-descriptions-item label="人工备注" :span="2">
              <div class="payload-block">{{ detailForm.manualRemark || '-' }}</div>
            </a-descriptions-item>
            <a-descriptions-item label="投保人快照" :span="2">
              <a-textarea :value="detailForm.applicantSnapshot || ''" :auto-size="{ minRows: 3, maxRows: 8 }" read-only />
            </a-descriptions-item>
            <a-descriptions-item label="被保人快照" :span="2">
              <a-textarea :value="detailForm.insuredSnapshot || ''" :auto-size="{ minRows: 4, maxRows: 10 }" read-only />
            </a-descriptions-item>
            <a-descriptions-item label="第三方请求报文" :span="2">
              <a-textarea :value="detailForm.thirdRequestPayload || ''" :auto-size="{ minRows: 5, maxRows: 12 }" read-only />
            </a-descriptions-item>
            <a-descriptions-item label="第三方响应报文" :span="2">
              <a-textarea :value="detailForm.thirdResponsePayload || ''" :auto-size="{ minRows: 5, maxRows: 12 }" read-only />
            </a-descriptions-item>
          </a-descriptions>
        </a-spin>
      </a-modal>
    </div>
  </div>
</template>

<script>
import { parseTime } from '@/utils/ruoyi'
import { getUnderwritingDetail } from '@/api/insuranceOrder/insuranceOrder'

const bondPayStatusMap = {
  1: '待支付',
  2: '已支付',
  3: '已取消'
}

const bondPayStatusColorMap = {
  1: 'orange',
  2: 'green',
  3: 'red'
}

const underwritingStatusMap = {
  0: '未触发投保',
  1000: '待投保',
  1100: '投保中',
  1200: '投保成功',
  1250: '部分成功',
  1300: '投保失败待处理',
  1400: '投保失败',
  1500: '已取消'
}

const underwritingStatusColorMap = {
  0: 'blue',
  1000: 'cyan',
  1100: 'processing',
  1200: 'green',
  1250: 'gold',
  1300: 'orange',
  1400: 'red',
  1500: 'purple'
}

const cancelledAfterInsuredMap = {
  0: '未取消',
  1: '已取消(已投保后退保)'
}

const cancelledAfterInsuredColorMap = {
  0: 'default',
  1: 'purple'
}

const insuranceDepositForfeitedMap = {
  0: '归师傅(可退)',
  1: '归平台(违约)'
}

const insuranceDepositForfeitedColorMap = {
  0: 'green',
  1: 'red'
}

export default {
  name: 'InsuranceInfo',
  props: {
    orderId: { type: [Number, String], default: null },
    insuranceInfo: { type: Object, default: null }
  },
  data () {
    return {
      bondPayStatusMap,
      bondPayStatusColorMap,
      underwritingStatusMap,
      underwritingStatusColorMap,
      detailVisible: false,
      detailLoading: false,
      detailForm: {},
      insuredColumns: [
        { title: '姓名', dataIndex: 'name', key: 'name', width: 110, ellipsis: true },
        { title: '身份证号', dataIndex: 'idCard', key: 'idCard', ellipsis: true },
        { title: '性别', dataIndex: 'sex', key: 'sex', width: 80, scopedSlots: { customRender: 'sex' } },
        { title: '出生日期', dataIndex: 'birthday', key: 'birthday', width: 120, scopedSlots: { customRender: 'birthday' } }
      ],
      policyColumns: [
        { title: '被保人', dataIndex: 'insuredName', width: 260, scopedSlots: { customRender: 'policyInsured' } },
        { title: '投保状态', dataIndex: 'underwritingStatus', width: 140, scopedSlots: { customRender: 'policyUnderwritingStatus' } },
        { title: '已投保后退保', dataIndex: 'cancelledAfterInsured', width: 160, scopedSlots: { customRender: 'policyCancelledAfterInsured' } },
        { title: '保险保证金去向', dataIndex: 'insuranceDepositForfeited', width: 160, scopedSlots: { customRender: 'policyInsuranceDepositForfeited' } },
        { title: '保单状态', dataIndex: 'policyFileUrl', width: 140, scopedSlots: { customRender: 'policyStatus' } },
        { title: '操作', dataIndex: 'action', width: 100, scopedSlots: { customRender: 'policyAction' } }
      ]
    }
  },
  computed: {
    hasInsurance () {
      return !!(this.insuranceInfo && this.insuranceInfo.id)
    },
    applicantInfo () {
      return this.parseJsonObject(this.insuranceInfo && this.insuranceInfo.applicantSnapshot)
    },
    insuredList () {
      const list = this.parseJsonArray(this.insuranceInfo && this.insuranceInfo.insuredSnapshot)
      return list.map((item, index) => ({
        ...item,
        tableKey: `${item.id || index}-${item.idCard || ''}`
      }))
    },
    // v3.2 业务调整：未投保人数 = insuredSnapshot 长度 - 已下发白鸽的保单明细数（粗略估算，已投保后退保的仍按差额显示）
    pendingInsuredCount () {
      if (!this.insuranceInfo || !this.insuranceInfo.id) return 0
      const snapshot = this.parseJsonArray(this.insuranceInfo.insuredSnapshot)
      const policyList = Array.isArray(this.insuranceInfo.policyList) ? this.insuranceInfo.policyList : []
      const diff = snapshot.length - policyList.length
      return diff > 0 ? diff : 0
    },
    // v3.2 业务调整：是否已超过"开工前 90 分钟"阈值
    isWorkStartNearTimeout () {
      const workStart = this.insuranceInfo && this.insuranceInfo.workStartTime
      if (!workStart) return false
      const now = new Date().getTime()
      const ws = new Date(workStart).getTime()
      if (isNaN(ws)) return false
      // 距开工 < 90 分钟视为接近临界
      return (ws - now) <= 90 * 60 * 1000
    }
  },
  methods: {
    formatDateTime (value) {
      return value ? parseTime(value, '{y}-{m}-{d} {h}:{i}:{s}') : '-'
    },
    hasPolicyList (record) {
      return Array.isArray(record && record.policyList) && record.policyList.length > 0
    },
    policyListRows (record) {
      return this.hasPolicyList(record) ? record.policyList : []
    },
    policyProgressText (record) {
      const total = this.policyTotalCount(record)
      const uploaded = this.policyUploadedCount(record)
      return total > 0 ? `已上传 ${uploaded}/${total}` : '-'
    },
    policyTotalCount (record) {
      if (record && record.policyTotalCount != null) {
        return Number(record.policyTotalCount) || 0
      }
      return this.hasPolicyList(record) ? record.policyList.length : 0
    },
    policyUploadedCount (record) {
      if (record && record.policyUploadedCount != null) {
        return Number(record.policyUploadedCount) || 0
      }
      if (!this.hasPolicyList(record)) {
        return record && record.policyFileUrl ? 1 : 0
      }
      return record.policyList.filter(item => item && item.policyFileUrl).length
    },
    parseJsonObject (value) {
      if (!value || typeof value !== 'string') return null
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
      } catch (e) {
        return null
      }
    },
    parseJsonArray (value) {
      if (!value || typeof value !== 'string') return []
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    sexText (sex) {
      if (sex === '男' || sex === '女') return sex
      if (Number(sex) === 2) return '女'
      if (sex != null && sex !== '') return '男'
      return '-'
    },
    parseBirthday (idCard) {
      if (!idCard || typeof idCard !== 'string') return ''
      const normalized = idCard.trim()
      if (normalized.length < 14) return ''
      const birthday = normalized.slice(6, 14)
      if (!/^\d{8}$/.test(birthday)) return ''
      return `${birthday.slice(0, 4)}-${birthday.slice(4, 6)}-${birthday.slice(6, 8)}`
    },
    openDetail () {
      if (!this.hasInsurance) return
      this.detailVisible = true
      this.detailLoading = true
      getUnderwritingDetail({ id: this.insuranceInfo.id })
        .then(res => {
          if (res && res.success) {
            this.detailForm = res.data || {}
          } else {
            this.detailForm = {}
          }
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    closeDetail () {
      this.detailVisible = false
      this.detailForm = {}
    },
    viewPolicy (record) {
      const url = record && record.policyFileUrl
      if (!url) {
        this.$message.warning('暂无保单文件')
        return
      }
      window.open(url, '_blank')
    }
  }
}
</script>

<style lang="less" scoped>
.order-detail-tab {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.order-detail-tab .tab-content {
  width: 100%;
  padding: 16px;
  align-self: flex-start;
}

.insurance-block-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.insurance-section {
  margin-top: 16px;
}

.detail-link {
  padding: 0;
  height: auto;
}

.payload-block {
  white-space: pre-wrap;
  word-break: break-all;
}

.policy-summary {
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.65);
}

.policy-table {
  margin-top: 4px;
}

.policy-card-no,
.policy-upload-time {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.pending-hint {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
</style>
